import { PageConfig } from "./pageConfig.types";
import { StoredViewSummary } from "./formViewStorage.types";

/**
 * Which view a page opens on, and which views it offers - kept together so the two
 * agree. A page whose shipped default is deactivated with nothing stored has no view
 * at all, which is a real state rather than a fault.
 */

/** The page's shipped default: its layout and what it is called. */
export interface ShippedView {
  config: PageConfig;
  name: string;
}

export const DefaultViewName = "Default";

/** The shipped default's view id, which has no stored row to carry one. */
export const DefaultFormId = "default";

export interface ViewListEntry {
  /** The slug, which is what the URL and the picker's selection carry. */
  key: string;
  label: string;
  /** The row this entry came from, or null for the shipped config. */
  storedId: string | null;
  /** Switched off - the shipped default for this organisation, or a stored view. */
  deactivated?: boolean;
}

export type ViewTarget =
  | { kind: "stored"; summary: StoredViewSummary }
  | { kind: "shipped" };

/**
 * The designer still lists what has been switched off - the shipped default and any
 * stored view - because it is the only place either is switched back on. A page must
 * not offer either.
 */
export interface ViewListOptions {
  defaultDeactivated?: boolean;
  /** True for the designer's own picker. Defaults to the runtime's stricter view. */
  includeDeactivated?: boolean;
}

export function buildViewList(
  shipped: ShippedView | undefined,
  stored: StoredViewSummary[],
  { defaultDeactivated, includeDeactivated }: ViewListOptions = {}
): ViewListEntry[] {
  const entries: ViewListEntry[] = stored
    .filter((view) => includeDeactivated || !view.deactivated)
    .map((view) => ({
      key: view.viewId,
      label: view.name,
      storedId: view.id,
      deactivated: view.deactivated,
    }));

  const showsDefault =
    shipped && (!defaultDeactivated || includeDeactivated);

  if (shipped && showsDefault) {
    entries.push({
      key: shipped.config.viewId,
      label: shipped.name,
      storedId: null,
      deactivated: defaultDeactivated,
    });
  }

  return entries;
}

/**
 * What an id resolves to, or null when the page has nothing to show. An unknown id
 * falls back to the default, or to the first stored view when the default is off.
 */
export function resolveViewTarget(
  viewId: string | undefined,
  shipped: ShippedView | undefined,
  stored: StoredViewSummary[],
  { defaultDeactivated, includeDeactivated }: ViewListOptions = {}
): ViewTarget | null {
  const wanted = (viewId || "").toLowerCase();
  const match = stored.find((view) => view.viewId.toLowerCase() === wanted);

  if (match) {
    return { kind: "stored", summary: match };
  }

  const shippedAvailable =
    shipped && (!defaultDeactivated || includeDeactivated);

  // Asked for the shipped view while it is off: the designer may open it, a page may not.
  if (shipped && wanted === shipped.config.viewId.toLowerCase()) {
    return shippedAvailable ? { kind: "shipped" } : fallback(stored);
  }

  if (shippedAvailable) {
    return { kind: "shipped" };
  }

  return fallback(stored);
}

function fallback(stored: StoredViewSummary[]): ViewTarget | null {
  return stored.length > 0 ? { kind: "stored", summary: stored[0] } : null;
}
